import axios from "axios";
import { KeyRound, LogOut, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { toast } from "../components/ui/sonner";
import { fetchMe, formatApiError, loginAccount, logoutAccount, registerAccount, updateAccountName, updatePreferences } from "../utils/auth";
import { getDailyStreak, getLocalPlays, getLocalPoints, getTubeCount } from "../utils/rewards";
import { TIERS } from "./Rewards";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const resetToken = searchParams.get("reset");
  const [user, setUser] = useState(() => location.state?.user ?? undefined); // undefined = checking
  const [mode, setMode] = useState(resetToken ? "reset" : "login"); // login | register | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [callsign, setCallsign] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [pals, setPals] = useState(null);
  const [badges, setBadges] = useState([]);
  const [points, setPoints] = useState(() => getLocalPoints());

  useEffect(() => {
    if (user !== undefined) return;
    fetchMe().then(setUser);
  }, [user]);

  useEffect(() => {
    const onAuth = (e) => setUser(e.detail.user);
    const onEarn = (e) => setPoints(e.detail.total);
    window.addEventListener("dolo-auth", onAuth);
    window.addEventListener("dolo-rewards", onEarn);
    return () => {
      window.removeEventListener("dolo-auth", onAuth);
      window.removeEventListener("dolo-rewards", onEarn);
    };
  }, []);

  useEffect(() => {
    if (!user?.tag) return;
    axios
      .get(`${API}/pals/save/${encodeURIComponent(user.tag)}`)
      .then(({ data }) => setPals(data.state || null))
      .catch(() => setPals(null));
    axios
      .get(`${API}/rewards/${encodeURIComponent(user.tag)}`)
      .then(({ data }) => setBadges(data.badges || []))
      .catch(() => {});
  }, [user?.tag]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (mode === "forgot") {
        const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
        setNotice(data.detail || "IF THAT EMAIL IS REGISTERED, A RECOVERY LINK IS ON ITS WAY.");
      } else if (mode === "reset") {
        if (password !== passwordConfirm) {
          setError("PASSWORDS DO NOT MATCH.");
          return;
        }
        const { data } = await axios.post(`${API}/auth/reset-password`, { token: resetToken, password });
        setNotice(data.detail || "PASSWORD RESET — JACK IN WITH THE NEW ONE.");
        setPassword("");
        setPasswordConfirm("");
        setSearchParams({}, { replace: true });
        setMode("login");
      } else {
        const authed = mode === "register" ? await registerAccount(email, password, callsign) : await loginAccount(email, password);
        setUser(authed);
        setNameDraft(authed.name || "");
        toast.success(mode === "register" ? "ACCOUNT FORGED — WELCOME TO THE MESH." : "SIGNED IN — PROGRESS MERGED.");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const saveName = async () => {
    setBusy(true);
    try {
      const updated = await updateAccountName(nameDraft);
      setUser(updated);
      toast.success("CALLSIGN UPDATED — THE LEADERBOARD SEES IT NOW.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await logoutAccount();
    setUser(null);
    toast.success("SIGNED OUT — YOUR STASH STAYS VAULTED.");
  };

  const toggleDropAlerts = async (event) => {
    const want = event.target.checked;
    setBusy(true);
    try {
      const updated = await updatePreferences(want);
      setUser(updated);
      toast.success(want ? "DROP ALERTS ON — YOU'LL HEAR ABOUT DROPS FIRST." : "DROP ALERTS OFF — BADGE-FIRST ACCESS STILL APPLIES IF YOU FORGE ONE.");
    } catch {
      toast.error("SIGNAL LOST — PREFERENCE NOT SAVED.");
    } finally {
      setBusy(false);
    }
  };

  const plays = getLocalPlays();
  const gamesPlayed = Object.values(plays).reduce((a, b) => a + b, 0);
  const tubes = getTubeCount();
  const streak = getDailyStreak();

  const tierInfo = useMemo(() => {
    const next = TIERS.find((t) => points < t.pts);
    const current = [...TIERS].reverse().find((t) => points >= t.pts);
    const floor = current ? current.pts : 0;
    const pct = next ? Math.min(100, Math.round(((points - floor) / (next.pts - floor)) * 100)) : 100;
    return { next, current, pct };
  }, [points]);

  const palsCount = pals?.collection?.length ?? null;
  const crownsTotal = pals?.crowns ? Object.values(pals.crowns).reduce((a, b) => a + b, 0) : null;
  const bestRun = Number(localStorage.getItem("wasteland-run-best") || 0);
  const riotBest = Math.max(
    Number(localStorage.getItem("riotball-high-red") || 0),
    Number(localStorage.getItem("riotball-high-white") || 0),
  );

  const guestTitles = { login: "JACK IN", register: "FORGE ACCOUNT", forgot: "SEND RECOVERY LINK", reset: "SET NEW PASSWORD" };

  return (
    <main className="account-page" data-testid="account-page">
      <section className="account-hero">
        <span className="rewards-eyebrow">RUNNER DOSSIER // ACCOUNT</span>
        <h1>
          {user ? (
            <>
              HAIL, <em>{(user.name || "RUNNER").toUpperCase()}</em>
            </>
          ) : (
            <>
              YOUR <em>ACCOUNT</em>
            </>
          )}
        </h1>
        <p>
          One account keeps your whole wasteland alive — points, streaks, badges, your DoloPals crew, and board crowns
          follow you to any device you sign in from.
        </p>
      </section>

      {user === undefined ? (
        <p className="account-loading" data-testid="account-loading">PINGING THE VAULT…</p>
      ) : !user ? (
        <Reveal className="account-card" data-testid="account-auth-card">
          {mode === "forgot" || mode === "reset" ? null : (
            <div className="account-tabs">
              <button
                type="button"
                className={mode === "login" ? "is-active" : ""}
                onClick={() => {
                  setMode("login");
                  setError("");
                  setNotice("");
                }}
                data-testid="account-login-tab"
              >
                LOG IN
              </button>
              <button
                type="button"
                className={mode === "register" ? "is-active" : ""}
                onClick={() => {
                  setMode("register");
                  setError("");
                  setNotice("");
                }}
                data-testid="account-register-tab"
              >
                SIGN UP
              </button>
            </div>
          )}
          {mode === "forgot" ? (
            <p className="account-note" data-testid="account-forgot-intro">
              Drop your email and the vault will transmit a one-hour recovery link.
            </p>
          ) : null}
          {mode === "reset" ? (
            <p className="account-note" data-testid="account-reset-intro">
              Recovery link accepted — it works once, within the hour. Set a new password below.
            </p>
          ) : null}
          <form onSubmit={submit} className="account-form" data-testid="account-auth-form">
            {mode === "register" ? (
              <input
                value={callsign}
                onChange={(e) => setCallsign(e.target.value)}
                placeholder="CALLSIGN (SHOWN ON THE LEADERBOARD)"
                maxLength={24}
                disabled={busy}
                data-testid="account-callsign-input"
              />
            ) : null}
            {mode !== "reset" ? (
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="EMAIL"
                required
                disabled={busy}
                data-testid="account-email-input"
              />
            ) : null}
            {mode === "login" || mode === "register" || mode === "reset" ? (
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "register" ? "PASSWORD (6+ CHARACTERS)" : mode === "reset" ? "NEW PASSWORD (6+ CHARACTERS)" : "PASSWORD"}
                required
                minLength={6}
                disabled={busy}
                data-testid="account-password-input"
              />
            ) : null}
            {mode === "reset" ? (
              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="CONFIRM NEW PASSWORD"
                required
                minLength={6}
                disabled={busy}
                data-testid="account-password-confirm-input"
              />
            ) : null}
            {error ? (
              <p className="account-error" data-testid="account-error">
                {error}
              </p>
            ) : null}
            {notice ? (
              <p className="account-notice" data-testid="account-notice">
                {notice}
              </p>
            ) : null}
            <button type="submit" className="account-submit" disabled={busy} data-testid="account-submit-button">
              <KeyRound size={14} /> {busy ? "PATCHING IN…" : guestTitles[mode]}
            </button>
          </form>
          {mode === "login" ? (
            <button
              type="button"
              className="account-forgot-link"
              onClick={() => {
                setMode("forgot");
                setError("");
                setNotice("");
              }}
              data-testid="account-forgot-link"
            >
              LOST YOUR PASSWORD? RECOVER THE STASH →
            </button>
          ) : null}
          {mode === "forgot" || mode === "reset" ? (
            <button
              type="button"
              className="account-forgot-link"
              onClick={() => {
                setMode("login");
                setError("");
                setNotice("");
                if (resetToken) setSearchParams({}, { replace: true });
              }}
              data-testid="account-back-login"
            >
              ← BACK TO LOGIN
            </button>
          ) : null}
          {mode === "login" || mode === "register" ? (
            <>
              <div className="account-divider">OR</div>
              <button type="button" className="account-google" onClick={googleLogin} data-testid="account-google-button">
                CONTINUE WITH GOOGLE
              </button>
            </>
          ) : null}
          <p className="account-note">
            Signing in merges this device's Titan Tag into your account — points, streaks, and badges carry over
            automatically. Your current stash: {points} PTS.
          </p>
        </Reveal>
      ) : (
        <div className="account-dashboard" data-testid="account-dashboard">
          <Reveal className="account-card">
            <div className="account-id-row">
              <span className="account-email" data-testid="account-email">{user.email}</span>
              {user.tag ? (
                <span className="account-tag" data-testid="account-tag">TAG {user.tag}</span>
              ) : null}
            </div>
            <div className="account-name-row">
              <input
                value={nameDraft || user.name || ""}
                onChange={(e) => setNameDraft(e.target.value)}
                maxLength={24}
                disabled={busy}
                data-testid="account-name-input"
                aria-label="Display callsign"
              />
              <button type="button" onClick={saveName} disabled={busy} data-testid="account-name-save-button">
                <Save size={13} /> SAVE CALLSIGN
              </button>
            </div>
            <p className="account-note">Your callsign shows on the Top Runners board and the Hall of Fame.</p>
          </Reveal>

          <Reveal className="account-card">
            <h2>PROGRESS</h2>
            <div className="account-stats">
              <div className="account-stat" data-testid="account-stat-points">
                <strong>{points}</strong>
                <span>STASH PTS</span>
              </div>
              <div className="account-stat" data-testid="account-stat-streak">
                <strong>{streak}</strong>
                <span>DAY STREAK</span>
              </div>
              <div className="account-stat" data-testid="account-stat-games">
                <strong>{gamesPlayed}</strong>
                <span>GAMES PLAYED</span>
              </div>
              <div className="account-stat" data-testid="account-stat-tubes">
                <strong>{tubes}</strong>
                <span>TUBES STOCKED</span>
              </div>
            </div>
            <div className="account-tier" data-testid="account-tier-progress">
              <div className="account-tier-head">
                <span>
                  TIER: <strong>{tierInfo.current ? tierInfo.current.title : "NO TIER YET"}</strong>
                </span>
                <span>
                  {tierInfo.next ? `${points} / ${tierInfo.next.pts} PTS TO ${tierInfo.next.title}` : "MAX TIER REACHED"}
                </span>
              </div>
              <div className="account-tier-bar">
                <span style={{ width: `${tierInfo.pct}%` }} />
              </div>
            </div>
          </Reveal>

          <Reveal className="account-card">
            <h2>ACHIEVEMENTS & CREW</h2>
            {badges.length > 0 ? (
              <div className="rewards-balance-badges account-badge-row" data-testid="account-badges">
                {badges.map((b) => (
                  <b key={b}>{b}</b>
                ))}
              </div>
            ) : (
              <p className="account-note" data-testid="account-badges-empty">
                No badges forged yet — streak 7 days, land an AL Riotball hat trick, pull a Legendary, take a board crown, or
                break 1000m in Wasteland Run.
              </p>
            )}
            <ul className="account-achievements">
              <li data-testid="account-best-run">
                BEST SIGNAL <strong>{bestRun > 0 ? `${bestRun}m` : "—"}</strong>
              </li>
              <li data-testid="account-riot-best">
                AL RIOTBALL BEST <strong>{riotBest > 0 ? `${riotBest} GOALS` : "—"}</strong>
              </li>
              <li data-testid="account-pals-count">
                DOLOPALS CREW <strong>{palsCount !== null ? `${palsCount} COLLECTED` : "—"}</strong>
              </li>
              <li data-testid="account-crowns">
                BOARD CROWNS <strong>{crownsTotal !== null ? crownsTotal : "—"}</strong>
              </li>
            </ul>
            <div className="account-links">
              <Link to="/rewards" data-testid="account-rewards-link">DOLO REWARDS →</Link>
              <Link to="/dolopals" data-testid="account-dolopals-link">KIDZONE →</Link>
            </div>
          </Reveal>

          <button type="button" className="account-logout" onClick={logout} data-testid="account-logout-button">
            <LogOut size={14} /> SIGN OUT
          </button>
        </div>
      )}

      <Footer />
    </main>
  );
}
